import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GradeBadge, StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Search, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import DeleteOrRequestButton from "@/components/DeleteOrRequestButton";

const STATUSES = ["active", "on_hold", "churned"];

export default function Clients() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [grade, setGrade] = useState(params.get("grade") || "");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", {
        params: {
          page, page_size: 50,
          q: q || undefined,
          status: status || undefined,
          grade: grade || undefined,
        },
      });
      setRows(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [page, status, grade]);
  useEffect(() => {
    const t = setTimeout(reload, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const isAdmin = user?.role === "crm_manager";
  const isBM = user?.role === "branch_manager";
  const showActions = isAdmin || isBM;

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="clients-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="mptg-overline">Accounts</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Clients</h1>
          <p className="text-slate-500 mt-1">{total} client{total === 1 ? "" : "s"} in portfolio</p>
        </div>
        {isAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="create-client-btn">
                <Plus className="w-4 h-4 mr-1" /> New Client
              </Button>
            </DialogTrigger>
            <CreateClientDialog onCreated={() => { setCreateOpen(false); reload(); }} />
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="mptg-card flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search client name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="client-search-input"
          />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="client-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={grade || "all"} onValueChange={(v) => setGrade(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="client-grade-filter"><SelectValue placeholder="Grade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All grades</SelectItem>
            {["Alpha", "Beta", "Gama"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="mptg-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full mptg-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Industry</th>
                <th>HQ</th>
                <th>Cost center</th>
                <th>Campaigns</th>
                <th>Grade</th>
                <th>Status</th>
                {showActions && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={showActions ? 8 : 7} className="text-center py-10 text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={showActions ? 8 : 7} className="text-center py-10 text-slate-400">No clients match.</td></tr>
              ) : (
                rows.map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`}>
                    <td>
                      <Link to={`/clients/${c.id}`} className="font-semibold text-slate-800 hover:text-[#2E86C1]">
                        {c.name}
                      </Link>
                      <div className="text-xs text-slate-500 truncate max-w-[260px]">{c.primary_contact_name}</div>
                    </td>
                    <td className="text-slate-600">{c.industry || "-"}</td>
                    <td className="text-slate-600">{c.hq_location || "-"}</td>
                    <td className="text-slate-600 font-mono text-xs">{c.cost_center_code || <span className="text-slate-300">—</span>}</td>
                    <td className="text-slate-600 tabular-nums">{c.campaign_count}</td>
                    <td><GradeBadge grade={c.current_grade || c.latest_grade} /></td>
                    <td><StatusBadge status={c.status} /></td>
                    {showActions && (
                      <td className="text-right whitespace-nowrap space-x-3">
                        {(isAdmin || isBM) && (
                          <button
                            onClick={() => setEditRow(c)}
                            className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1"
                            data-testid={`edit-client-${c.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> {isAdmin ? "Edit" : "Set grade"}
                          </button>
                        )}
                        <DeleteOrRequestButton
                          entityType="client"
                          entity={c}
                          onDeleted={reload}
                          onRequested={() => toast.info("Request submitted — visible under Requests")}
                        />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editRow && (
        <Dialog open={true} onOpenChange={(v) => !v && setEditRow(null)}>
          <EditClientDialog row={editRow} isBM={isBM} onDone={() => { setEditRow(null); reload(); }} />
        </Dialog>
      )}
    </div>
  );
}

function CreateClientDialog({ onCreated }) {
  const [f, setF] = useState({
    name: "", industry: "", hq_location: "", cost_center_code: "",
    current_grade: "",
    primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
    status: "active",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    if (f.cost_center_code && !/^\d{1,6}$/.test(f.cost_center_code)) {
      toast.error("Cost center code must be numeric, max 6 digits"); return;
    }
    setSaving(true);
    try {
      const payload = { ...f };
      if (!payload.current_grade) delete payload.current_grade;
      if (!payload.cost_center_code) delete payload.cost_center_code;
      await api.post("/clients", payload);
      toast.success("Client created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const bind = (k) => ({ value: f[k] || "", onChange: (e) => setF({ ...f, [k]: e.target.value }) });

  return (
    <DialogContent className="max-w-xl" data-testid="create-client-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">New Client</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div className="col-span-2">
          <Label>Name *</Label>
          <Input {...bind("name")} data-testid="input-client-name" />
        </div>
        <div>
          <Label>Industry</Label>
          <Input {...bind("industry")} data-testid="input-client-industry" />
        </div>
        <div>
          <Label>HQ Location</Label>
          <Input {...bind("hq_location")} />
        </div>
        <div>
          <Label>Cost center code</Label>
          <Input
            {...bind("cost_center_code")}
            inputMode="numeric"
            maxLength={6}
            placeholder="e.g. 100245"
            data-testid="input-cost-center"
          />
          <div className="text-[11px] text-slate-400 mt-1">Numeric, max 6 digits</div>
        </div>
        <div>
          <Label>Current grade</Label>
          <Select value={f.current_grade || "none"} onValueChange={(v) => setF({ ...f, current_grade: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="input-current-grade"><SelectValue placeholder="— ungraded —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— ungraded —</SelectItem>
              {["Alpha", "Beta", "Gama"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[11px] text-slate-400 mt-1">Set manually based on Branch Manager understanding</div>
        </div>
        <div>
          <Label>Primary contact name</Label>
          <Input {...bind("primary_contact_name")} />
        </div>
        <div>
          <Label>Primary contact email</Label>
          <Input type="email" {...bind("primary_contact_email")} />
        </div>
        <div>
          <Label>Phone</Label>
          <Input {...bind("primary_contact_phone")} />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-client-btn">
          {saving ? "Saving…" : "Create"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}


function EditClientDialog({ row, onDone, isBM }) {
  const [f, setF] = useState({
    name: row.name || "",
    industry: row.industry || "",
    hq_location: row.hq_location || "",
    cost_center_code: row.cost_center_code || "",
    current_grade: row.current_grade || "",
    primary_contact_name: row.primary_contact_name || "",
    primary_contact_email: row.primary_contact_email || "",
    primary_contact_phone: row.primary_contact_phone || "",
    status: row.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!isBM && !f.name) { toast.error("Name required"); return; }
    if (!isBM && f.cost_center_code && !/^\d{1,6}$/.test(f.cost_center_code)) {
      toast.error("Cost center code must be numeric, max 6 digits"); return;
    }
    setSaving(true);
    try {
      const payload = isBM
        ? { current_grade: f.current_grade || null }
        : { ...f, current_grade: f.current_grade || null,
            cost_center_code: f.cost_center_code || null };
      await api.patch(`/clients/${row.id}`, payload);
      toast.success("Client updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const bind = (k) => ({ value: f[k] || "", onChange: (e) => setF({ ...f, [k]: e.target.value }) });

  if (isBM) {
    return (
      <DialogContent className="max-w-md" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Set grade — {row.name}</DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <Label>Current grade</Label>
          <Select value={f.current_grade || "none"} onValueChange={(v) => setF({ ...f, current_grade: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="edit-current-grade"><SelectValue placeholder="— ungraded —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— ungraded —</SelectItem>
              {["Alpha", "Beta", "Gama"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[11px] text-slate-400">
            Branch Managers can update only the client grade. Set manually based on your understanding — there is no automatic logic.
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-grade-btn">
            {saving ? "Saving…" : "Save grade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="max-w-xl" data-testid="edit-client-dialog">
      <DialogHeader><DialogTitle className="font-heading">Edit {row.name}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div className="col-span-2"><Label>Name *</Label><Input {...bind("name")} data-testid="edit-client-name" /></div>
        <div><Label>Industry</Label><Input {...bind("industry")} /></div>
        <div><Label>HQ Location</Label><Input {...bind("hq_location")} /></div>
        <div>
          <Label>Cost center code</Label>
          <Input {...bind("cost_center_code")} inputMode="numeric" maxLength={6} placeholder="e.g. 100245" data-testid="edit-cost-center" />
          <div className="text-[11px] text-slate-400 mt-1">Numeric, max 6 digits</div>
        </div>
        <div>
          <Label>Current grade</Label>
          <Select value={f.current_grade || "none"} onValueChange={(v) => setF({ ...f, current_grade: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="edit-current-grade"><SelectValue placeholder="— ungraded —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— ungraded —</SelectItem>
              {["Alpha", "Beta", "Gama"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Primary contact name</Label><Input {...bind("primary_contact_name")} /></div>
        <div><Label>Primary contact email</Label><Input type="email" {...bind("primary_contact_email")} /></div>
        <div><Label>Phone</Label><Input {...bind("primary_contact_phone")} /></div>
        <div><Label>Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-edit-client">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
